import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { formatTranslation } from 'intl';
import { LevelEntity } from 'shared/domain/level/types/entity';
import { SiteEntity } from 'shared/domain/site/types/entity';
import { ISO6391LanguageCode } from 'shared/types/locale';
import {
  displayDataGridDate,
  displayDataGridDateWithTime,
} from 'shared/utils/date/dates';
import { Typography } from '@mui/material';
import { ELLIPSIS_TEXT_SX } from 'components/common/dataGrid/styles';
import { ListCellWithModal } from './listCellWithModal';
import { IntlShape } from 'react-intl';
import { isNil } from 'helpers/misc';
import { generateLabelList } from 'helpers/generators';
import { CommonTranslationKeys } from 'intl/keys';
import { toArrayValue } from 'shared/utils/array';
import { memo, useMemo } from 'react';

function renderCellText(value: string): JSX.Element {
  return (
    <Typography variant='body2' sx={ELLIPSIS_TEXT_SX}>
      {value}
    </Typography>
  );
}

const MemoizedListCellWithModal = memo(ListCellWithModal);

const SiteCell = memo(function SiteCell({
  value,
  label,
}: {
  value: unknown;
  label: string;
}) {
  const sites = useMemo(() => toArrayValue<SiteEntity>(value), [value]);

  return (
    <MemoizedListCellWithModal
      items={sites}
      label={label}
      emptyPlaceholder=''
    />
  );
});

const LevelCell = memo(function LevelCell({
  value,
  label,
}: {
  value: unknown;
  label: string;
}) {
  const levels = useMemo(
    () => toArrayValue<LevelEntity>(value),
    [value]
  );

  return (
    <MemoizedListCellWithModal
      items={levels}
      label={label}
      emptyPlaceholder=''
    />
  );
});

const ContractPartiesCell = memo(function ContractPartiesCell({
  value,
}: {
  value: unknown;
}) {
  const parties = useMemo(
    () => toArrayValue<{ _id: string; label: string }>(value),
    [value]
  );
  const text = useMemo(
    () => (parties.length > 0 ? generateLabelList(parties) : '-'),
    [parties]
  );

  return renderCellText(text);
});

export function createDateWithTimeCellRenderer(
  locale: ISO6391LanguageCode,
  timezone: string
) {
  return (params: GridRenderCellParams) => {
    const dateString = displayDataGridDateWithTime(
      params.value,
      timezone,
      locale
    );

    if (isNil(dateString)) return null;

    return renderCellText(dateString);
  };
}

export function createDateCellRenderer(
  locale: ISO6391LanguageCode,
  timezone: string
) {
  return (params: GridRenderCellParams) => {
    const dateString = displayDataGridDate(params.value, timezone, locale);

    if (isNil(dateString)) return null;

    return renderCellText(dateString);
  };
}

export function createSiteCellRenderer(intl: IntlShape) {
  const label = formatTranslation(intl, CommonTranslationKeys.site);

  return (params: GridRenderCellParams) => {
    return <SiteCell value={params.value} label={label} />;
  };
}

export function createLevelCellRenderer(intl: IntlShape) {
  const label = formatTranslation(intl, CommonTranslationKeys.level);

  return (params: GridRenderCellParams) => {
    return <LevelCell value={params.value} label={label} />;
  };
}

export function createDocumentStateCellRenderer() {
  return (params: GridRenderCellParams) => {
    if (isNil(params.formattedValue)) return null;

    if (typeof params.formattedValue !== 'string') return null;

    return renderCellText(params.formattedValue);
  };
}

export function createContractPartiesCellRenderer() {
  return (params: GridRenderCellParams) => {
    return <ContractPartiesCell value={params.value} />;
  };
}
